import Vue from 'vue';
import AjaxUtils from '../../util/ajax_utils';
import Store from '../../data/store';
import { removeEmptyVals, objsAreEqual } from '../_ui/_utils/useObjectUtils';
import { ISO_3166_NAMES } from '../../util/iso3166_tools';

import {
    ApckTrustRisksColumn,
    ApckTrustDomainsColumn,
    ApckTrustHighestRiskColumn,
    ApckTrustThirdpartyDetailColumn,
    ApckTrustCountryColumn
} from './trust_columns';

export const ApckTrustRelationshipsTable = {
    props: {
        src: {
            type: String,
            default: '/trust/relationships/list'
        }
    },
    data() {
        return {
            loading: false,
            convertThirdpartyId: null,
            convertHostname: null,
            suppressHostname: null,
            filters: {
                domain: '',
                risk_level: '',
                country: '',
                thirdparty: '',
                date_from: null,
                date_to: null
            },
            availableFilters: {
                domains: [],
                riskLevels: [
                    { value: 5, label: 'Highest' },
                    { value: 4, label: 'High' },
                    { value: 3, label: 'Medium' },
                    { value: 2, label: 'Low' },
                    { value: 1, label: 'Informational' },
                    { value: 0, label: 'Unknown' }
                ],
                countries: []
            },
            suggestions: {
                countries: [],
                thirdparty: []
            },
            cols: [
                {"data": "thirdparty", "heading": "Third Party", "ctype": "trust-thirdparty-detail"},
                {"data": "vulns", "heading": "Vulnerabilities", "ctype": "trust-relationship-risks", "width": 220},
                {"data": "domain_count", "heading": "Domain Count", "width": 100, "center": true},
                {"data": "country", "heading": "Country", "ctype": "trust-country-column", "width":280},
                {"data": "risks", "heading": "Trusts", "ctype": "trust-relationship-risks", "width": 220},
            ],
            actions: [
                {
                    title: 'View Third Party Details',
                    icon: 'fa-eye',
                    class: 'btn-secondary',
                    handler: this.viewDetails,
                    useOwnCol: true,
                    hideLabel: true,
                },
                {
                    title: "Build Detailed Trust Report For Third Party (Can take a few moments to generate)",
                    icon: "fa-download",
                    class: "btn-secondary",
                    handler: this.exportThirdpartyReport,
                    useOwnCol: true,
                    hideLabel: true,
                },
                {
                    title: "Convert to a monitored domain, I manage this third party (removes this third party record and adds it to the monitored domains list)",
                    icon: "fa-arrow-right-arrow-left",
                    class: "btn-secondary",
                    handler: this.convertToDomain,
                    useOwnCol: true,
                    hideLabel: true,
                },
                {
                    title: "Suppress this third party from showing up in the list and add it to the suppression control lists",
                    icon: "fa-ban",
                    class: "btn-secondary",
                    handler: this.suppressThirdparty,
                    useOwnCol: true,
                    hideLabel: true,
                }
            ]
        };
    },
    computed: {
        tableSrc() {
            return this.src;
        },
        filterOptions() {
            return {
                domains: [
                    {
                        label: 'All Domains',
                        value: '',
                    },
                    ...this.availableFilters.domains.map((item) => ({
                        label: item.value,
                        value: item.value,
                    })),
                ],
                riskLevels: [
                    {
                        label: 'All Trust Levels',
                        value: '',
                    },
                    ...this.availableFilters.riskLevels.map((item) => ({
                        label: item.label,
                        value: String(item.value),
                    })),
                ],
            }
        },
        riskFilterLabel() {
            const selectedLevel = this.filters.risk_level;
            const matchingRiskLevel = this.filterOptions.riskLevels.find(level => level.value === selectedLevel);
            return matchingRiskLevel ? matchingRiskLevel.label : this.filterOptions.riskLevels[0].label;
        },
        domainFilterLabel() {
            const selectedDomain = this.filters.domain;
            const matchingDomain = this.filterOptions.domains.find(domain => domain.value === selectedDomain);
            return matchingDomain ? matchingDomain.label : this.filterOptions.domains[0].label;
        },
        tableKey() {
            return `relations-table-${this._uid}`
        },
        hasFilters() {
            if(this.dropdownsChangedToAll()) return true
            return Object.keys(removeEmptyVals(this.filters)).length > 0
        },
        hasRouteQuery() {
            return Object.keys(this.$route.query).length > 0
        },
        queriesMatch() {
            const activeFilters = removeEmptyVals(this.filters)
            const currentQuery = removeEmptyVals(this.$route.query)

            return objsAreEqual(activeFilters, currentQuery)
        },
        disableClear() {
            return !this.hasFilters && !this.hasRouteQuery
        },
        disableSubmit() {
            return this.disableClear || this.queriesMatch
        },
    },
    methods: {
        viewDetails: function(rec) {
            const thirdparty = rec.get('thirdparty');
            window.location.href = `/trust/thirdparty/${thirdparty._id}/view`;
        },
        exportThirdpartyReport: function(rec) {
            let cmp = this;
            const thirdparty = rec.get('thirdparty');
            if (!thirdparty || !thirdparty.hostname) {
                Store.Flash.error("Cannot generate report: thirdparty hostname is missing.");
                return;
            }

            cmp.loading = true;

            // Build query parameters
            let params = {
                thirdparty: thirdparty.hostname
            };

            let queryString = Object.keys(params).map(key =>
                encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
            ).join('&');

            let url = '/trust/export/pdf?' + queryString;

            // Use fetch API for better download handling (cookies will be sent automatically)
            fetch(url, {
                credentials: 'same-origin' // Ensure cookies are sent
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                // Get the filename from the Content-Disposition header if available
                let filename = `trust-report-${thirdparty.hostname}.pdf`;
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        // Remove quotes if present
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                return response.blob().then(blob => ({ blob, filename }));
            })
            .then(({ blob, filename }) => {
                // Create a URL for the blob and trigger download
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename; // Use the server-provided filename
                document.body.appendChild(link);
                link.click();

                // Clean up
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);

                cmp.loading = false;
            })
            .catch(error => {
                console.error('Error downloading thirdparty report:', error);
                cmp.loading = false;
                Store.Flash.error("An error occurred while downloading the PDF report.");

                // Fallback to direct URL navigation
                console.warn('Falling back to direct URL navigation');
                window.location.href = url;
            });
        },
        convertToDomain: function(rec) {
            const thirdparty = rec.get('thirdparty');
            if (!thirdparty || !thirdparty._id) {
                Store.Flash.error("Cannot convert: thirdparty data is missing.");
                return;
            }
            this.convertThirdpartyId = thirdparty._id;
            this.convertHostname = thirdparty.hostname || '';
            this.$nextTick(() => {
                if (this.$refs.convertBtn) {
                    this.$refs.convertBtn.openModal();
                }
            });
        },
        suppressThirdparty: function(rec) {
            const thirdparty = rec.get('thirdparty');
            if (!thirdparty || !thirdparty.hostname) {
                Store.Flash.error("Cannot suppress: thirdparty data is missing.");
                return;
            }
            this.suppressHostname = thirdparty.hostname;
            this.$nextTick(() => {
                if (this.$refs.suppressBtn) {
                    this.$refs.suppressBtn.openModal();
                }
            });
        },
        exportData() {
            let cmp = this;
            cmp.loading = true;

            // Build query parameters from filters
            let params = {};
            Object.keys(this.filters).forEach(key => {
                if (this.filters[key] !== null && this.filters[key] !== '') {
                    params[key] = this.filters[key];
                }
            });

            let queryString = Object.keys(params).map(key =>
                encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
            ).join('&');

            let url = '/trust/reporting/thirdparties?' + queryString;

            // Use fetch API for better download handling (cookies will be sent automatically)
            fetch(url, {
                credentials: 'same-origin' // Ensure cookies are sent
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                // Get the filename from the Content-Disposition header if available
                let filename = 'trust-relationships-report.xlsx';
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        // Remove quotes if present
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }

                return response.blob().then(blob => ({ blob, filename }));
            })
            .then(({ blob, filename }) => {
                // Create a URL for the blob and trigger download
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename; // Use the server-provided filename
                document.body.appendChild(link);
                link.click();

                // Clean up
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);

                cmp.loading = false;
            })
            .catch(error => {
                console.error('Error downloading filtered report:', error);
                cmp.loading = false;
                Store.Flash.error("An error occurred while downloading the Excel report.");

                // Fallback to direct URL navigation
                console.warn('Falling back to direct URL navigation');
                window.location.href = url;
            });
        },
        setDomainFilter(value) {
            this.filters.domain = value || null
        },
        setRiskFilter(value) {
            this.filters.risk_level = value || null
        },
        setCountryFilter(value) {
            this.filters.country = value ? value.toUpperCase() : ''
        },
        setThirdPartyFilter(value) {
            this.filters.thirdparty =  value ? value.toLowerCase() : ''
        },
        handleSubmit() {
            if(this.disableSubmit) return

            if(this.hasFilters) {
                this.applyFilters()
            } else {
                this.clearFilters()
            }
        },
        dropdownsChangedToAll() {
            return (
                (!this.filters.domain && this.$route.query.domain)
                || (!this.filters.risk_level && this.$route.query.risk_level)
            )
        },
        clearFilters() {
            this.filters = {
                domain: '',
                risk_level: '',
                country: '',
                thirdparty: '',
                date_from: null,
                date_to: null
            };

            if(this.hasRouteQuery) {
                this.$router.push({
                    query: {}
                })
            }

            this.applyFilters()
        },
        getFiltersFromUrl() {
            if(!this.hasRouteQuery) return;

            const originalFilters = this.filters
            const queryFilters = removeEmptyVals(this.$route.query);

            this.filters = { ...originalFilters, ...queryFilters};
        },
        updateUrlQuery() {
            if (this.hasFilters && !this.queriesMatch) {
                this.$router.push({
                    query: removeEmptyVals(this.filters)
                })
            }
        },
        applyFilters() {
            if (this.$refs.relationshipsTable) {

                this.updateUrlQuery()

                // Build query parameters from filters
                let params = {};
                Object.keys(this.filters).forEach(key => {
                    params[key] = this.filters[key];
                });

                // Update table with filters
                this.$refs.relationshipsTable.$data.store.load(params);
            }
        },
        async loadFilterOptions() {
            // Load available domains for filter dropdown
            const cmp = this;

            await AjaxUtils.ajax_get({
                url: '/trust/scope',
                success: (data) => {
                    if (data && data.success) {
                        cmp.availableFilters.domains = data.data || [];
                    }
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        fetchThirdpartySuggestions() {
             AjaxUtils.ajax_get({
                //Don't limit the results
                url:  this.tableSrc + '?limit=1000',
                success: (data) => {
                    if(data?.success) {
                        const items = data.items.flat()
                        this.suggestions.thirdparty = [...new Set(
                            items
                                .map(item => item.thirdparty.hostname)
                                .filter(thirdparty => thirdparty)
                        )];
                    }
                },
                error: (data) => {
                    console.error('Failed to fetch suggestions:', data)
                },
                opts: {
                    dataType: 'json',
                    flash_form_errors: false,
                    show_success_flash: false,
                    flash_404_errors: false
                }
            });
        },
        fetchCountrySuggestions() {
            this.suggestions.countries = Object.entries(ISO_3166_NAMES)
                .map(([key, value]) => {
                    return {
                        label: `${key} - ${value}`,
                        flag: key,
                        value: key
                    }
                });
            },
    },
    created() {
        // Register custom column components
        Vue.component('apck-trust-relationship-risks', ApckTrustRisksColumn);
        Vue.component('apck-trust-domains', ApckTrustDomainsColumn);
        Vue.component('apck-trust-highest-risk', ApckTrustHighestRiskColumn);
        Vue.component('apck-trust-thirdparty-detail', ApckTrustThirdpartyDetailColumn);
        Vue.component('apck-trust-country-column', ApckTrustCountryColumn);
    },
    async mounted() {
        await this.loadFilterOptions();
        this.getFiltersFromUrl()
        this.applyFilters();
        this.fetchThirdpartySuggestions();
        this.fetchCountrySuggestions();
    },
    template: `
        <ui-page title="Third Party Trusts" stickyTitle>
            <template #title-bar-buttons>
                <ui-button
                    label="Third Party Suppliers Report"
                    iconLeft="fa-file-excel"
                    type="outline"
                    @click.prevent="exportData"
                />
            </template>
            <template #content>
                <ui-layout direction="column">
                    <ui-layout direction="column" justify="start">
                        <ui-layout gap="2" align="center">
                            <ui-icon type="secondary" icon="fa-filter" />
                            <ui-heading tag="span" size="md">Filters</ui-heading>
                        </ui-layout>
                        
                        <ui-layout
                            tag="form"
                            gap="4"
                            align="end"
                            name="trust_relationship_filter"
                            @keydown.enter.native.prevent="handleSubmit"
                            @submit.native.prevent="handleSubmit"
                        >
                            <ui-input-text
                                label="Third Party Domain"
                                placeholder="Enter third party domain"
                                type="text"
                                query="thirdparty"
                                :modelValue="filters.thirdparty"
                                :suggestions="suggestions.thirdparty"
                                :suggestionLimit="2"
                                @update:modelValue="setThirdPartyFilter"
                            />
                            
                            <ui-input-text
                                label="Country"
                                placeholder="eg: United Kingdom"
                                type="text"
                                query="country"
                                :modelValue="filters.country"
                                :suggestions="suggestions.countries"
                                :suggestionLimit="1"
                                @update:modelValue="setCountryFilter"
                            />
                            
                            <ui-dropdown-radio
                                name="domainFilter"
                                :modelValue="filters.domain"
                                :options="filterOptions.domains"
                                :label="domainFilterLabel"
                                align="left"
                                @update:modelValue="setDomainFilter"
                            />
                            
                            <ui-dropdown-radio
                                name="riskFilter"
                                :modelValue="filters.risk_level"
                                :options="filterOptions.riskLevels"
                                :label="riskFilterLabel"
                                align="left"
                                @update:modelValue="setRiskFilter"
                            />

                            <ui-button
                                label="Submit"
                                type="primary"
                                :disabled="disableSubmit"
                                nativeType="submit"
                            />

                            <ui-button
                                label="Clear"
                                type="primary"
                                :color="disableClear ? null : 'red'"
                                :disabled="disableClear"
                                @click.prevent="clearFilters"
                            />
                        </ui-layout>
                    </ui-layout>
                    
                    <apck-table
                        ref="relationshipsTable"
                        class="trust-relationships-table"
                        :columns="cols"
                        :src="tableSrc"
                        :actions="actions"
                        :limit="100"
                        :border="false"
                        :autoload="false"
                        :key="tableKey"
                        :show-toolbar="false"
                        :sglRowAction="viewDetails"
                        newStyleTable
                    />
                </ui-layout>
                
                <ui-spinner :show="loading" theme="dark" />

                <apck-convert-to-domain-button
                    v-if="convertThirdpartyId"
                    ref="convertBtn"
                    :thirdpartyId="convertThirdpartyId"
                    :hostname="convertHostname"
                    :hideLabel="true"
                    style="display: none;"
                />

                <apck-quick-suppress-button
                    v-if="suppressHostname"
                    ref="suppressBtn"
                    :hostname="suppressHostname"
                    suppressionType="thirdparty"
                    :hideLabel="true"
                    style="display: none;"
                />
            </template>
        </ui-page>
    `
}
